import { useCallback, useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { useDialer } from '@/contexts/DialerContext';
import { useIncomingCallSession } from '@/hooks/useIncomingCallSession';
import { ApiErrorException } from '@/api/client';
import {
  cancelQualification, getQualification, reconcileCalls, submitQualification,
  type CallToReconcile, type PadOutcome,
} from '@/api/endpoints/qualifications';
import { emitQualificationSaved, type CallPool } from '@/state/callPool';
import { clearNoteDraft, useCallPageStore, type QualifyingCall } from '@/state/callPageStore';
import { applyMute } from '@/services/dialer/muteCall';
import { logCallPage, newCorrelationId } from '@/services/dialer/callPage/correlationLog';

/**
 * The call page's commands (spec §6): the only front writer of a call's
 * outcome and note on the new page. Session moves go through the dialer's
 * existing handlers; the qualification goes to the server workflow
 * (POST /api/calls/:id/qualifications, ENG-2), which applies it after the
 * 3 s countdown and runs the side effects then.
 */

export const COUNTDOWN_MS = 3000;
/**
 * The server commits COMMIT_DELAY_MS (3 s, server/src/domain/calling/
 * qualification.ts) after it receives the qualification. « Annuler » stops
 * a little earlier, counted from when the request left.
 */
const UNDO_MARGIN_MS = 300;
const SUBMIT_TIMEOUT_MS = 8000;
const MAX_RECONCILE = 3;

export class CallPageError extends Error {
  constructor(readonly code: string, readonly correlationId: string, message: string) {
    super(message);
    this.name = 'CallPageError';
  }
}

export interface SaveInput {
  parallelCallId: string;
  outcome: PadOutcome;
  note?: string;
  followUpAt?: string;
  timezone?: string;
  /** « Enregistrer et rester en pause »: no countdown, no next series. */
  stayPaused?: boolean;
  /** The call being qualified, put back on screen if « Annuler » wins. */
  call?: QualifyingCall | null;
  /** CEO-12: what the AI suggested, and whether it was saved as is. */
  suggestedOutcome?: PadOutcome | null;
}

type Dialer = {
  dialerRunState: string;
  handleManualStartDialing: () => void;
  handleResumeDialing: () => boolean;
  handleStopDialing: () => void;
  handleStopAll: () => Promise<void> | void;
  handleHangupAndContinue: () => Promise<void>;
  setConnectedContact: (v: null) => void;
  setShowCallSheet: (v: boolean) => void;
  setIsMuted: (v: boolean) => void;
  isMuted: boolean;
  showKeypad: boolean;
  setShowKeypad: (v: boolean) => void;
  dialingSessionStartedAt?: number | null;
};

const isNetworkFailure = (err: unknown) => !(err instanceof ApiErrorException);

async function withTimeout<T>(run: (signal: AbortSignal) => Promise<T>): Promise<T> {
  const ctl = new AbortController();
  const timer = setTimeout(() => ctl.abort(), SUBMIT_TIMEOUT_MS);
  try { return await run(ctl.signal); } finally { clearTimeout(timer); }
}

export function useCallCommands() {
  const d = useDialer() as unknown as Dialer;
  const inbound = useIncomingCallSession();
  const queryClient = useQueryClient();
  const { t } = useTranslation(['dialer']);

  const refresh = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: ['parallel-calls'] });
    void queryClient.invalidateQueries({ queryKey: ['all-calls'] });
    void queryClient.invalidateQueries({ queryKey: ['prospect-queue'] });
  }, [queryClient]);

  const startSession = useCallback(() => {
    useCallPageStore.getState().reset();
    d.handleManualStartDialing();
  }, [d]);

  const pauseSession = useCallback(() => {
    useCallPageStore.getState().setResumeAfter(null);
    d.handleStopDialing();
  }, [d]);
  const resumeSession = useCallback(() => { d.handleResumeDialing(); }, [d]);

  const stopSession = useCallback(async (hasAudibleCall: boolean) => {
    const store = useCallPageStore.getState();
    store.setCountdown(null);
    store.setResumeAfter(null);
    if (hasAudibleCall) d.handleStopDialing();
    else await d.handleStopAll();
    store.setSummary({ endedAt: Date.now(), startedAt: d.dialingSessionStartedAt ?? null });
  }, [d]);

  /**
   * Hangs up the audible call; the page moves to its qualification. An
   * answered inbound call ends through its own session (CALL-PILL-1), an
   * outbound one through the session handlers, which pause the session.
   */
  const hangup = useCallback(async (call: QualifyingCall | null, isInbound = false) => {
    const store = useCallPageStore.getState();
    if (call) store.setQualifying({ ...call, endedAt: Date.now() });
    // Only an outbound hang-up in a running session pauses it; the mark stays
    // until the last waiting call is qualified (an inbound call answered in
    // between does not clear it).
    if (!isInbound && call && d.dialerRunState === 'running') store.setResumeAfter(call.parallelCallId);
    logCallPage({ action: 'hangup', parallelCallId: call?.parallelCallId ?? '' });
    if (isInbound) await inbound.handleHangup();
    else await d.handleHangupAndContinue();
  }, [d, inbound]);

  const saveQualification = useCallback(async (input: SaveInput): Promise<'persisted' | 'unknown'> => {
    const store = useCallPageStore.getState();
    // Same content as a save whose result is unknown: same id (a replay).
    const key = JSON.stringify([input.outcome, input.note?.trim() ?? '', input.followUpAt ?? '', input.timezone ?? '']);
    const unsent = store.unsent;
    const qualificationId = unsent && unsent.parallelCallId === input.parallelCallId && unsent.key === key
      ? unsent.qualificationId : newCorrelationId();
    const sentAt = Date.now();
    const previous = store.saved.find((s) => s.parallelCallId === input.parallelCallId);
    const body = {
      qualification_id: qualificationId,
      outcome: input.outcome,
      ...(input.note && input.note.trim() ? { note: input.note } : {}),
      ...(input.followUpAt ? { follow_up_at: input.followUpAt, timezone: input.timezone } : {}),
      ...(previous ? { expected_revision: previous.revision } : {}),
      ...(input.suggestedOutcome ? { suggested_outcome: input.suggestedOutcome, suggestion_accepted: input.suggestedOutcome === input.outcome } : {}),
    };
    const base = { correlationId: qualificationId, parallelCallId: input.parallelCallId, outcome: input.outcome };

    let view = null as Awaited<ReturnType<typeof submitQualification>> | null;
    for (let attempt = 1; attempt <= MAX_RECONCILE && !view; attempt += 1) {
      try {
        view = await withTimeout((signal) => submitQualification(input.parallelCallId, body, signal));
      } catch (err) {
        if (!isNetworkFailure(err)) {
          const code = (err as ApiErrorException).code;
          store.setUnsent(null);
          // Stale: align on the revision that stands, so the next save passes.
          const current = (err as ApiErrorException).error?.context?.current_revision;
          if (code === 'QUALIFICATION_STALE' && typeof current === 'number') {
            if (previous) store.removeSaved(previous.qualificationId);
            if (current > 0) store.addSaved({ ...(previous ?? { qualificationId: `stale-${input.parallelCallId}`, outcome: input.outcome, name: '' }), parallelCallId: input.parallelCallId, revision: current });
          }
          logCallPage({ ...base, action: 'qualify', status: 'failed', code, attempt });
          throw new CallPageError(code || 'QUALIF_SAVE_FAILED', qualificationId, (err as Error).message);
        }
        // The request may have reached the server: ask before sending again
        // (same qualification_id, so a resend is a replay anyway).
        try { view = await withTimeout((signal) => getQualification(qualificationId, signal)); } catch { /* not stored, or still offline */ }
        logCallPage({ ...base, action: 'qualify', status: view ? 'reconciled' : 'retry', attempt });
      }
    }
    if (!view) {
      store.setUnsent({ parallelCallId: input.parallelCallId, qualificationId, key });
      logCallPage({ ...base, action: 'qualify', status: 'unknown' });
      return 'unknown';
    }
    store.setUnsent(null);

    logCallPage({ ...base, action: 'qualify', status: 'persisted', revision: view.revision });
    const qualifying = input.call ?? store.qualifying;
    // The old sources let go of the call: the machine leaves `qualified`
    // (RUI-P3) and the context no longer holds a contact on the line.
    emitQualificationSaved((window as unknown as { __callPool?: CallPool }).__callPool, input.parallelCallId, { call_outcome: input.outcome });
    d.setConnectedContact(null);
    d.setShowCallSheet(false);
    clearNoteDraft(input.parallelCallId);
    store.addSaved({
      qualificationId, parallelCallId: input.parallelCallId, revision: view.revision, outcome: input.outcome,
      name: qualifying?.name ?? '', followUpAt: input.followUpAt,
    });
    store.nextQualifying();
    // The next series starts only from a session the hang-up paused, and only
    // once no call set aside waits for its own outcome. A call qualified with
    // the session stopped (summary, reconciliation, inbound outside a
    // session) never dials anything.
    const another = !!useCallPageStore.getState().qualifying;
    const marked = !!useCallPageStore.getState().resumeAfter;
    const resumes = !input.stayPaused && !another && d.dialerRunState === 'paused' && marked;
    // Consumed once no call waits; « rester en pause » drops it.
    if (!another || input.stayPaused) store.setResumeAfter(null);
    const undoUntil = sentAt + COUNTDOWN_MS - UNDO_MARGIN_MS;
    store.setCountdown({
      qualificationId, parallelCallId: input.parallelCallId, outcome: input.outcome,
      endsAt: resumes ? Date.now() + COUNTDOWN_MS : undoUntil, held: false, undoUntil, resumes,
      call: qualifying ?? null, previous: previous ?? null,
    });
    // A correction may leave things the server cannot undo (DES-17): read
    // them once it has committed, and say so.
    if (previous) {
      const wait = Math.max(0, Date.parse(view.commit_at) - Date.now()) + 1500;
      setTimeout(() => {
        void getQualification(qualificationId).then((after) => {
          const effects = after.effects ?? [];
          if (effects.includes('meeting_manual_fix')) toast.warning(t('dialer:callPage.toast.meetingManualFix'));
          if (effects.includes('crm_manual_fix')) toast.warning(t('dialer:callPage.toast.crmManualFix'));
        }).catch(() => { /* the correction stands; the notice is best effort */ });
      }, wait);
    }
    refresh();
    return 'persisted';
  }, [d, refresh, t]);

  /** The countdown ends: the paused session resumes, or the banner just goes. */
  const next = useCallback(() => {
    const { countdown, setCountdown } = useCallPageStore.getState();
    if (!countdown?.resumes || d.dialerRunState !== 'paused') { setCountdown(null); return; }
    // A dial still in flight: keep the banner, held, for « Continuer ».
    if (d.handleResumeDialing()) setCountdown(null);
    else setCountdown({ ...countdown, held: true });
  }, [d]);

  /** Espace: 3 s more before the next series (« Annuler » keeps its window). */
  const extendCountdown = useCallback(() => {
    const { countdown, setCountdown } = useCallPageStore.getState();
    if (countdown?.resumes) setCountdown({ ...countdown, endsAt: Math.max(countdown.endsAt, Date.now()) + COUNTDOWN_MS });
  }, []);

  /** Échap, or a dialog opening: the next series waits; the qualification stands. */
  const holdCountdown = useCallback(() => {
    const { countdown, setCountdown } = useCallPageStore.getState();
    // A countdown that resumes nothing has nothing to hold: it just ends.
    if (countdown?.resumes && !countdown.held) setCountdown({ ...countdown, held: true });
  }, []);

  /** « Annuler (U) »: cancels the pending qualification on the server. */
  const undoQualification = useCallback(async () => {
    const store = useCallPageStore.getState();
    const c = store.countdown;
    if (!c) return;
    if (Date.now() > c.undoUntil) {
      // Too late: the countdown (and its resume) stays as it was.
      toast.error(t('dialer:callPage.toast.undoTooLate'));
      return;
    }
    store.setCountdown(null);
    try {
      await cancelQualification(c.qualificationId);
      store.removeSaved(c.qualificationId);
      // A cancelled correction leaves the committed one in place.
      if (c.previous) store.addSaved(c.previous);
      if (c.call) store.setQualifying(c.call);
      // The call is back to qualify: its save resumes the session again.
      if (c.resumes) store.setResumeAfter(c.parallelCallId);
      logCallPage({ action: 'undo', correlationId: c.qualificationId, parallelCallId: c.parallelCallId, status: 'cancelled' });
      toast.info(t('dialer:callPage.toast.undone'));
    } catch (err) {
      const code = err instanceof ApiErrorException ? err.code : 'NETWORK';
      logCallPage({ action: 'undo', correlationId: c.qualificationId, parallelCallId: c.parallelCallId, status: 'failed', code });
      toast.error(code === 'QUALIFICATION_NOT_PENDING' ? t('dialer:callPage.toast.undoTooLate') : t('dialer:callPage.toast.undoFailed'));
      // The qualification stands: keep « Continuer » for a session to resume.
      if (c.resumes) store.setCountdown({ ...c, held: true, undoUntil: 0 });
    }
    refresh();
  }, [refresh, t]);

  /** Spec §4.3 CALL_STATE_UNKNOWN: settle before dialing again. */
  const reconcile = useCallback(async (): Promise<CallToReconcile[]> => {
    const { items } = await reconcileCalls();
    refresh();
    logCallPage({ action: 'reconcile', status: String(items.length) });
    return items;
  }, [refresh]);

  const toggleMute = useCallback(() => {
    const nextMuted = !d.isMuted;
    if (!applyMute(nextMuted)) {
      toast.error(t('dialer:callBar.muteUnavailable', { defaultValue: 'Aucun appel actif à couper' }));
      return;
    }
    d.setIsMuted(nextMuted);
  }, [d, t]);

  const toggleKeypad = useCallback(() => { d.setShowKeypad(!d.showKeypad); }, [d]);

  /** « Qualifier maintenant »: a call of this session left without an outcome. */
  const qualifyLater = useCallback((call: QualifyingCall) => {
    const store = useCallPageStore.getState();
    store.setSummary(null);
    store.setResumeAfter(null);
    store.setQualifying(call);
  }, []);

  return useMemo(() => ({
    startSession, pauseSession, resumeSession, stopSession, hangup, saveQualification, next,
    extendCountdown, holdCountdown, undoQualification, reconcile, toggleMute, toggleKeypad, qualifyLater,
  }), [startSession, pauseSession, resumeSession, stopSession, hangup, saveQualification, next,
    extendCountdown, holdCountdown, undoQualification, reconcile, toggleMute, toggleKeypad, qualifyLater]);
}
